// Libs
import classNames from 'classnames/bind';
import { Avatar, Box, Button, Grid2, List, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
// Components, Layouts, Pages
import {
  BaseInputSearch,
  BasePagination,
  ConfirmModal,
  Loading,
} from '~/components/common';
import { DocumentItem, FormDocument } from '~/components/module/document';
import { FolderItem, FormFolder } from '~/components/module/folder';
// Others
import useDebounce from '~/utils/hooks/useDebounce';
import { useAppDispatch } from '~/utils/redux/hooks';
import { deleteFolder, getFolderList } from '~/thunks/folder/folderThunk';
import { folderActions, selectIsRefreshFolderList } from '~/thunks/folder/folderSlice';
import { documentActions, selectIsRefreshDocumentList } from '~/thunks/document/documentSlice';
import { IFolder, IFolderParams } from '~/utils/interface/folder';
import { IPaginationResponse } from '~/utils/interface/base';
import { IQueryParamBase } from '~/utils/interface/common';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { StorageEnum } from '~/utils/enum/base';
import { BusinessTypeEnum, RoleEnum } from '~/utils/enum/common';
import { IDocument, IDocumentByFolderParams } from '~/utils/interface/document';
import { deleteDocument, getDocumentListByFolder } from '~/thunks/document/documentThunk';
// Styles, images, icons
import styles from './CustomerDocuments.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const CustomerDocuments = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/document', 'common/base']);
  const dispatch = useAppDispatch();
  const { id: customerId } = useParams();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const filePageSelected = useMemo<number>(
    () => Number(params?.page ?? BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
    [params?.page]
  );
  const fileLimitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  const textSearchParams = useMemo<string>(
    () => String(params?.textSearch || EMPTY_STRING),
    [params?.textSearch]
  );
  const isFirstLoad = useRef(true);
  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  //#endregion Declare Hook

  //#region Selector
  const isRefreshFolderList = useSelector(selectIsRefreshFolderList);
  const isRefreshFileList = useSelector(selectIsRefreshDocumentList);
  //#endregion Selector

  //#region Declare State
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);

  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [isShowFormFile, setIsShowFormFile] = useState<boolean>(false);
  const [isShowFormFolder, setIsShowFormFolder] = useState<boolean>(false);

  const [documentSelected, setDocumentSelected] = useState<IDocument>();
  const [folderItemSelected, setFolderItemSelected] = useState<IFolder>();

  const [folderList, setFolderList] = useState<IFolder[]>([]);
  const [, setHasMoreFolder] = useState<boolean>(true);
  const [folderSelected, setFolderSelected] = useState<string>(EMPTY_STRING);

  const [isLoadingFile, setIsLoadingFile] = useState<boolean>(false);
  const [fileList, setFileList] = useState<IDocument[]>([]);
  const [filePagination, setFilePagination] = useState<IPaginationResponse>();
  const [paramObject, setParamObject] = useState<IQueryParamBase>({
    page: filePageSelected,
    limit: fileLimitPage,
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    setSearchKey(textSearchParams);
  }, [textSearchParams]);

  useEffect(() => {
    if (!isRefreshFolderList || !customerId) return;

    const params: IFolderParams = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      businessId: customerId,
      businessType: BusinessTypeEnum.CUSTOMER,
      isShareFolder: true,
    };

    handleGetFolderList(params);
    dispatch(folderActions.setRefreshFolderList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshFolderList]);

  useEffect(() => {
    if (!folderSelected) return;

    const params: IDocumentByFolderParams = {
      folderId: folderSelected,
      params: paramObject,
    };

    handleGetDocumentListByFolder(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject, folderSelected]);

  useEffect(() => {
    if (!isRefreshFileList || !folderSelected) return;

    const params: IDocumentByFolderParams = {
      folderId: folderSelected,
      params: paramObject,
    };

    handleGetDocumentListByFolder(params);
    dispatch(documentActions.setIsRefreshDocumentList(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshFileList]);

  useEffect(() => {
    if (!customerId) return;

    const params: IFolderParams = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      businessId: customerId,
      businessType: BusinessTypeEnum.CUSTOMER,
      isShareFolder: true,
    };

    handleGetFolderList(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!filePagination) return;

    setParamObject({
      ...paramObject,
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: fileLimitPage,
      ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
    });

    setSearchParams({
      page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
      limit: String(fileLimitPage),
      ...(searchKey ? { textSearch: debounceSearchKey } : {}),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);

  useEffect(() => {
    if (isFirstLoad.current && folderList?.length > DEFAULT_NUMBER_ZERO) {
      setFolderSelected(folderList[DEFAULT_NUMBER_ZERO]?.id);
      isFirstLoad.current = false;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderList?.length]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetFolderList = (params: IFolderParams) => {
    dispatch(getFolderList(params))
      .unwrap()
      .then((res) => {
        if (!res || !res?.data) return;

        const { responses } = res?.data;
        setFolderList(responses);

        // if (responses?.length <= DEFAULT_NUMBER_ZERO) {
        //   setHasMoreFolder(false);
        // } else {
        //   setFolderList((prev) => {
        //     const newFolders = responses?.filter(
        //       (newFolder) => !prev.some((oldFolder) => oldFolder.id === newFolder.id)
        //     );
        //     return isRefreshFolderList ? [...newFolders, ...prev] : [...prev, ...newFolders];
        //   });
        //   setFolderCurrentPage((prev) => prev + DEFAULT_NUMBER_ONE);
        // }
      })
      .catch((error) => {
        setHasMoreFolder(false);
      })
      .finally(() => {});
  };

  const handleGetDocumentListByFolder = (params: IDocumentByFolderParams) => {
    setIsLoadingFile(true);

    dispatch(getDocumentListByFolder(params))
      .unwrap()
      .then((res) => {
        const { pagination, responses } = res?.data;

        setFileList(responses);
        setFilePagination(pagination);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoadingFile(false);
      });
  };

  const handleShowAddFolder = () => {
    setFolderItemSelected(undefined);
    setIsShowFormFolder(true);
  };

  const handleShowAddFile = () => {
    setIsShowFormFile(true);
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleSelectFolder = (folder: IFolder) => {
    if (!folder?.id) return;

    setFolderSelected(folder.id);
  };

  const handleEditFolder = (folder: IFolder) => {
    setFolderItemSelected(folder);
    setIsShowFormFolder(true);
  };

  const handleDeleteFolder = (folder: IFolder) => {
    if (!folder) return;

    setFolderItemSelected(folder);
    setIsShowConfirmDelete(true);
  };

  const handleCloseConfirmDelete = () => {
    setIsShowConfirmDelete(false);
    setFolderItemSelected(undefined);
  };

  const handleConfirmDelete = () => {
    if (folderItemSelected) {
      handleConfirmDeleteFolder();
    } else if (documentSelected) {
      handleConfirmDeleteDocument();
    }
  };

  const handleConfirmDeleteDocument = () => {
    if (!documentSelected?.id) return;

    setIsLoading(true);

    dispatch(deleteDocument(documentSelected?.id))
      .unwrap()
      .then((res) => {
        dispatch(documentActions.setIsRefreshDocumentList(true));
        handleCloseFormFile();
      })
      .catch((error) => {})
      .finally(() => {
        setIsShowConfirmDelete(false);
        setIsLoading(false);
      });
  };

  const handleConfirmDeleteFolder = () => {
    if (!folderItemSelected?.id) return;

    setIsLoading(true);

    dispatch(deleteFolder(folderItemSelected?.id))
      .unwrap()
      .then((res) => {
        dispatch(folderActions.setRefreshFolderList(true));

        if (folderItemSelected?.id === folderSelected) {
          setFolderSelected(
            folderList?.length > DEFAULT_NUMBER_ZERO ? folderList?.[DEFAULT_NUMBER_ZERO]?.id : EMPTY_STRING
          );
        }
      })
      .catch((error) => {})
      .finally(() => {
        setIsShowConfirmDelete(false);
        setFolderItemSelected(undefined);
        setIsLoading(false);
      });
  };

  const handleEditDocument = (file: IDocument) => {
    setDocumentSelected(file);
    setIsShowFormFile(true);
  };

  const handleDeleteDocument = (file: IDocument) => {
    setFolderItemSelected(undefined);
    setDocumentSelected(file);
    setIsShowConfirmDelete(true);
  };

  const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(fileLimitPage) });
  };

  const handleRowPerPageChange = (rowPerPage: number) => {
    setParamObject({ ...paramObject, page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: rowPerPage });
    setSearchParams({
      ...params,
      page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
      limit: String(rowPerPage),
    });
  };

  const handleCloseFormFile = () => {
    setDocumentSelected(undefined);
    setIsShowFormFile(false);
  };

  const handleCloseFormFolder = () => {
    setFolderItemSelected(undefined);
    setIsShowFormFolder(false);
  };
  //#endregion Handle Function

  return (
    <Grid2 component={'div'} id='customerDocumentsPage' className={cx('container')} container gap={'30px'}>
      <Grid2 size={4}>
        <Stack className={cx('group')} rowGap={'24px'} columnGap={'24px'}>
          <Box component={'div'} className={cx('fileUpload')}>
            <Button className={cx('buttonUpload')} onClick={handleShowAddFile}>
              <Stack
                direction={'row'}
                alignItems={'center'}
                justifyContent={'center'}
                gap={'10px'}
                flexWrap={'wrap'}
              >
                <Avatar src={icons.commonIconFileUpload} alt={t('common_alt_img', { ns: 'common/base' })} />
                <Typography component={'p'} className={cx('placeholder')}>
                  {t('component_file_upload_text_btn', { ns: 'common/base' })}
                  <Typography component={'span'} className={cx('placeholderBrowse')}>
                    {t('component_file_upload_browse_btn', { ns: 'common/base' })}
                  </Typography>
                </Typography>
              </Stack>
            </Button>
          </Box>

          <Stack component={'div'} className={cx('folderGroup')} rowGap={'20px'} columnGap={'20px'}>
            <Stack
              component={'div'}
              direction={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
              gap={'20px'}
              flexWrap={'wrap'}
              className={cx('folderTitleGroup')}
            >
              <Typography className={cx('title')}>{t('tab_documents_title_folder')}</Typography>

              <Button variant='contained' onClick={handleShowAddFolder}>
                {t('tab_documents_add_folder')}
              </Button>
            </Stack>

            <List component={'div'} id='listFolder' className={cx('listFolder')}>
              {/* <InfiniteScroll
                dataLength={folderList?.length}
                next={handleLoadMoreFolder}
                hasMore={hasMoreFolder}
                scrollableTarget={'listFolder'}
                loader={
                  <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} height={35}>
                    <CircularProgress size={14} />
                  </Stack>
                }
                endMessage={
                  folderList?.length === DEFAULT_NUMBER_ZERO && (
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} height={35}>
                      <Typography component={'p'} className={cx('noData')}>
                        {t('common_no_data_available', { ns: 'common/base' })}
                      </Typography>
                    </Stack>
                  )
                }
              > */}
              {folderList?.map((folder: IFolder, index: number) => (
                <FolderItem
                  key={index}
                  folder={folder}
                  index={index}
                  selected={folderSelected === folder.id}
                  showAction={
                    folder.isCurrentApply ||
                    folder.businessId === customerId ||
                    currentRole !== RoleEnum.TECHNICIAN
                  }
                  onSelect={handleSelectFolder}
                  onEdit={handleEditFolder}
                  onDelete={handleDeleteFolder}
                />
              ))}
              {/* </InfiniteScroll> */}
            </List>
          </Stack>
        </Stack>

        {/* <BaseAutocomplete
          label={t('tab_documents_label_tags')}
          multiple
          options={mockDataAutocomplete}
          onChange={handleTagsChange}
        /> */}
      </Grid2>

      <Grid2 size={'grow'}>
        <Stack
          direction={'column'}
          justifyContent={'space-between'}
          gap={'20px'}
          flexWrap={'wrap'}
          className={cx('documentTitleGroup')}
        >
          <Stack component={'div'} direction={'column'} gap={'20px'}>
            <Box component={'div'} className={cx('titleWrap')}>
              <Typography className={cx('title')}>{t('tab_documents_title')}</Typography>

              <BaseInputSearch value={searchKey} onChange={handleSearchChange} />
            </Box>

            <List className={cx('listDocument')}>
              {fileList?.map((file) => (
                <DocumentItem
                  key={file.id}
                  document={file}
                  onDelete={handleDeleteDocument}
                  onEdit={handleEditDocument}
                />
              ))}

              {isLoadingFile && <Loading />}
            </List>
          </Stack>

          {fileList?.length > DEFAULT_NUMBER_ZERO && (
            <BasePagination
              page={filePageSelected}
              count={filePagination?.totalPages}
              onChange={handlePageChange}
              onRowsPerPageChange={handleRowPerPageChange}
            />
          )}
        </Stack>
      </Grid2>

      {isShowFormFile && (
        <FormDocument
          businessType={BusinessTypeEnum.CUSTOMER}
          onClose={handleCloseFormFile}
          document={documentSelected}
        />
      )}

      {isShowFormFolder && (
        <FormFolder
          businessType={BusinessTypeEnum.CUSTOMER}
          onClose={handleCloseFormFolder}
          folder={folderItemSelected}
        />
      )}

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete_document', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}
    </Grid2>
  );
};

export default CustomerDocuments;
